'use client';

import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { InfoIcon } from '@/lib/components/InfoIcon';
import { AnimatedPercentText } from '@/lib/components/Text/AnimatedPercentText';
import { AnimatedUSDText } from '@/lib/components/Text/AnimatedUSDText';
import { useMatchingSharesAtPrice } from '@/lib/hooks/useMatchingSharesAtPrice';
import { useFees } from '@/lib/providers/FeesProvider';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import { EMarketAction } from '@/lib/types/market-action';
import { Text } from '@/lib/ui-kit/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { calculateLimitFee } from '@/lib/utils/calculateFees';

export const LimitBuySummary = () => {
  const t = useTranslations('orderForm');
  const limitPrice = useOrderFormStore.use.limitPrice();
  const shares = useOrderFormStore.use.sharesCount();
  const outcomeId = useOrderFormStore.use.selectedOutcomeId();
  const intent = useOrderFormStore.use.intent();
  const { fees } = useFees();

  const limitPriceUsd = useMemo(() => {
    return limitPrice.div(100);
  }, [limitPrice]);

  // Shares the book fills right away — they are charged the taker rate.
  const matchingShares = useMatchingSharesAtPrice({
    outcomeId: outcomeId ?? '',
    intent,
    action: EMarketAction.BUY,
    price: limitPriceUsd,
  });

  const totalSpend = useMemo(() => {
    if (limitPriceUsd.eq(0)) {
      return Decimal(0);
    }
    return limitPriceUsd.mul(shares);
  }, [limitPriceUsd, shares]);

  // Calculate fees
  const openingFee = useMemo(() => {
    if (!fees || totalSpend.eq(0)) {
      return Decimal(0);
    }
    const result = calculateLimitFee(
      limitPriceUsd,
      shares,
      matchingShares,
      EMarketAction.BUY,
      fees,
    );

    return result;
  }, [totalSpend, limitPriceUsd, shares, matchingShares, fees]);

  // For LIMIT: Total includes fees, Potential Payout doesn't

  const pnlPercent = useMemo(() => {
    if (totalSpend.eq(0)) {
      return Decimal(0);
    }
    // For LIMIT: Total includes fees, Potential Payout doesn't
    // Percentage: (Potential Payout - Total) / Total
    const totalCost = totalSpend.plus(openingFee);
    return shares.minus(totalCost).div(totalCost);
  }, [totalSpend, openingFee, shares]);

  if (!fees) {
    return null; // Don't render if fees are not loaded yet
  }

  return (
    <div className="flex flex-col gap-1">
      <Text variant="body-small" className="flex w-full items-center justify-between">
        <span className="flex items-center gap-1 text-v2-text-primary opacity-50">
          <Text>{t('total')}</Text>
        </span>
        <span className="text-v2-text-primary">
          <AnimatedUSDText value={totalSpend.plus(openingFee)} alwaysShowDecimals />
        </span>
      </Text>
      <Text variant="body-small" className="flex w-full items-center justify-between">
        <span className="flex items-center gap-1 text-v2-text-primary opacity-50">
          <Text>{t('potentialPayout')}</Text>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <InfoIcon />
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              sideOffset={8}
              className="!w-fit max-w-[300px] px-2 py-1.5 !text-left"
            >
              <Text variant="label-md">{t('potentialPayoutTooltip')}</Text>
            </TooltipContent>
          </Tooltip>
        </span>
        <span className="text-v2-green">
          <AnimatedUSDText value={shares} alwaysShowDecimals /> (
          <AnimatedPercentText value={pnlPercent} decimals={0} />)
        </span>
      </Text>
    </div>
  );
};
